<template>
  <form class="reset-password-form">
    <h1 class="text-center">Multi-factor Authentication</h1>
    <p class="text-center mb-2">Based on your email domain, your organization requires Multi-Factor Authentication (MFA) for continued access.
    <br/><br/>To proceed, please choose one of the available MFA methods below and complete the setup.</p>
    <lp-select label-top="Method" :options="method_options" v-model="method" />

    <div class="mfa-sms" v-if="method === MFAMethod.SMS">
      <lp-select label-top="Country" :options="country_options" v-model="country" />
      <div class="tel-input">
        <lp-input compact label-top="Phone" placeholder="(888) 888-8888" required type="tel" :max-length="11"
          :prefix="country_codes[country as keyof typeof country_codes]" v-model="phone" />
        <div class="mt-2 login-checkbox">
          <lp-checkbox type="primary" v-model="acceptCharges"
            label="I consent to receive SMS messages for Multi-factor authentication. Standard messaging rates may apply. " />
        </div>
      </div>
      <div class="ly-row mt-2 ly-even-2">
        <button @click="goBack" type="button"> Go Back</button>
        <lp-button @click="verify" type="primary" :loading="isSubmitting"
          :disabled="isSubmitting || !phone || phone?.length < 10 || !acceptCharges">Verify</lp-button>
      </div>
    </div>

    <div class="mfa-authapp" v-if="method === MFAMethod.Authenticator">
      <p class="text-center">To enroll your account, scan the QR code below using an authenticator app:</p>
      <div class="authenticator-placeholder text-center mt-2">
        <div class="text-center">
          <div class="container items-center"
            style="justify-content: center; display: flex; align-items: center; flex-direction: column;">
            <lp-spinner v-if="!qrSecret" data-ua-unique-id="ua-login-totp-form-spinner"
              style="margin-bottom: -130px;" />
            <canvas ref="QRCodeCanvas" />
          </div>
        </div>
      </div>

      <div class="mb-2">
        <p class="text-center mb-half">Can't read the QR code? <span @click="showSecret = !showSecret" class="mini-button">Use secret key</span></p>
        <div v-if="showSecret" class="mt-0 ly-row mfa-secret-container">
          <div class="w-95 border border-black-20 p-half ly-row ly-center-center rounded">
            <span>{{ mfaSecret }}</span>
          </div>
          <lp-tooltip content="Use this key in your authenticator app to generate your code" placement="top">
            <lp-button type="primary" icon="copy" @click="copyToClipboard" />
          </lp-tooltip>
        </div>
      </div>

      <p class="text-center">Enter the code to verify enrollment</p>
      <div class="mt-1 w-35 ly-container large-number-container mfa-input-container">
        <lp-input v-model="code" :max-length="6" class="text-center font-weight-bold" placeholder="______" />
      </div>
      <p class="text-center">Don't have an authenticator app? <br />
        Download an authenticator app like Google<br />
        Authenticator from <a href="https://apps.apple.com/us/app/google-authenticator/id388497605" target="_blank">App
          Store</a> or <a
          href="https://play.google.com/store/apps/details?id=com.google.android.apps.authenticator2&pli=1">Google
          Play</a></p>
      <div class="ly-row mt-2 ly-even-2">
        <button @click="goBack" type="button"> Go Back</button>
        <lp-button @click="verify" type="primary" :loading="isSubmitting"
          :disabled="isSubmitting || !code || code?.length !== 6">Verify</lp-button>
      </div>
    </div>

    <div class="msg msg-error w-100 text-center" v-if="errorMessage">
      {{ errorMessage }}
    </div>
  </form>
</template>
<script setup lang="ts">
import { inject } from 'vue'
import { Redirect } from '@/types/redirect'
import { ref, onMounted, watch, nextTick } from 'vue'
import { MFAMethod, MFACountry, getState, resetRedirectAfterMFA, initSmsMfa } from '@/services/mfa'
import { toCanvas } from 'qrcode'
import type { EnrollmentInput, EnrollmentResponse } from '@/services/mfa'
import type { AuthenticationResult } from '@/types/authenticationResult'

const method_options = [
  { name: 'SMS', value: MFAMethod.SMS },
  { name: 'Authenticator App', value: MFAMethod.Authenticator },
  /* { name: 'Email', value: MFAMethod.Email } */
]

const country_options = [
  { name: 'United States of America', value: MFACountry.US },
  { name: 'Canada', value: MFACountry.CA }
]

const country_codes = {
  [MFACountry.US]: '+1',
  [MFACountry.CA]: '+1'
}

const redirect = inject('redirect') as Redirect
const requestQrCodeSecret = inject('enrollAuthenticator') as ({ access_token }: { access_token: string }) => Promise<string | null>
const enrollSMS = inject('enrollSMS') as ({ username, phone_number, access_token }: EnrollmentInput) => Promise<{ session: string | null, sentTo: string | null }>
const verifyEnrollmentAuthenticator = inject('verifyEnrollmentAuthenticator') as ({ access_token, code }: { access_token: string, code: string }) => Promise<EnrollmentResponse>
const redirectToRetrieveToken = inject('redirectToRetrieveToken') as (authenticationResult: AuthenticationResult) => Promise<void>

const method = ref(MFAMethod.Authenticator)
const country = ref(MFACountry.US)
const errorMessage = ref('')
const acceptCharges = ref(false)
const qrSecret = ref<string | null>(null)
const QRCodeCanvas = ref(null)
const mfaSecret = ref<string | null>('')
const showSecret = ref(false)
const code = ref('')
const phone = ref('')
const { authenticationResult, username } = getState()
const isSubmitting = ref(false)

onMounted(async () => {
  qrSecret.value = await requestQrCodeSecret({ access_token: authenticationResult?.AccessToken || '' })
  genQRCode(qrSecret.value || '', username || '')
  mfaSecret.value = qrSecret.value
})

watch(method, (newMethod) => {
  if (newMethod === MFAMethod.Authenticator && qrSecret.value) {
    nextTick(() => QRCodeCanvas.value ? genQRCode(qrSecret.value!, username || '') : null)
  }
})

const genQRCode = (secret: string, username: string) => {
  if (!secret || !username || !QRCodeCanvas.value) return
  const path = `otpauth://totp/${username}?secret=${secret}&issuer=${window.location.host}`;
  toCanvas(QRCodeCanvas.value, path, (error: unknown) => {
    if (error) {
      console.error(error)
    }
  });
};

const goBack = () => redirect('LoginView')

const copyToClipboard = async (event: Event) => {
  event.preventDefault()

  try {
    await navigator.clipboard.writeText(mfaSecret.value ?? '')
  } catch(error) {
    console.error(error)
  }
}

const verify = async (event: Event) => {
  event.preventDefault()
  isSubmitting.value = true
  errorMessage.value = ''

  if (method.value === MFAMethod.SMS) {
    const countryCode = country_codes[country.value]
    const phone_number = `${countryCode}${phone.value}`

    const payload = {
      phone_number,
      username: username || '',
      access_token: authenticationResult?.AccessToken || ''
    }

    const { session, sentTo } = await enrollSMS(payload)
    if (!session || !sentTo) {
      isSubmitting.value = false
      errorMessage.value = 'Error starting the enrollment process, please try again later'
    }

    if (session && sentTo) initSmsMfa(session, sentTo)

    redirect('MFASMSConfirm')
  } else {
    const { success } = await verifyEnrollmentAuthenticator({
      access_token: authenticationResult?.AccessToken || '',
      code: code.value
    })

    if (!success) {
      isSubmitting.value = false
      errorMessage.value = 'Invalid MFA Code. The code you entered is incorrect or has expired.'
    }

    resetRedirectAfterMFA()
    if (success && authenticationResult) redirectToRetrieveToken(authenticationResult)
  }

  isSubmitting.value = false
}

</script>

<style>
.reset-password-form {
  margin: 0px 0px;
  padding: 0px 0px;
}

.mini-button {
  background-color: var(--indigo-10);
  border: 1px solid var(--indigo-20);
  border-radius: 4px;
  padding: 2px 4px;
  cursor: pointer;
  transition: all .3s;
}

.mini-button:hover {
  background-color: var(--indigo-20);
  border: 1px solid var(--indigo-30);
  transition: all .3s;
}

.mfa-secret-container span {
  font-size: 12px;
}
</style>
